import { ref, computed } from "vue"

export const TOTAL_QUESTIONS = 11

export const QUESTION_SCREENS = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 13, 14]

export const INSIGHT_TEXTS = {
  "laid-off": {
    title: "Let's build your 90-day action plan.",
    body: "The market moves fast after a RIF. These questions help us create a positioning + network activation strategy that moves before others realize the opportunity."
  },
  stalled: {
    title: "Let's understand your promotion case.",
    body: "We need to quantify your invisible impact and decide: do we make an internal case, or do we exit and prove yourself elsewhere?"
  },
  burnout: {
    title: "Let's design your sustainable move.",
    body: "Burnout isn't about comp—it's about energy fit. These questions help us identify the lifestyle + culture shifts that will actually stick."
  },
  pivot: {
    title: "Let's map your bridge into that industry.",
    body: "Industry transitions succeed through network + credibility, not by applying cold. These questions help us plan your stepping stone roles."
  },
  accelerate: {
    title: "Let's build your C-Suite playbook.",
    body: "C-Suite moves need board credibility, network, and comp strategy. These questions help us identify what you're missing and how to build it."
  }
}

export const CALLOUT_5_TEXTS = {
  "laid-off":
    "<strong>You're not alone—but you can't stall.</strong> The market moves fast after a layoff. Within 60–90 days, the best roles are filled and your narrative solidifies. Your leverage is highest now.",
  stalled:
    "<strong>The waiting game costs you.</strong> Every month you stay in a dead-end role, you lose leverage. The best internal or external opportunities go to people with clear moves. Act in the next 60–90 days.",
  burnout:
    "<strong>Burnout isn't a phase—it's a signal.</strong> The energy you lose now compounds. Your best move window is 60–90 days: act before resentment becomes your default.",
  pivot:
    "<strong>Industry transitions succeed on timing.</strong> You have 60–90 days to enter your new network while you're still \"the outsider with potential.\" After that, you're just another late switcher.",
  accelerate:
    "<strong>C-Suite moves demand momentum.</strong> The best board seats, executive roles, and investor networks open to people in motion. Your next 60–90 days determine what's available to you next."
}

export const CALLOUT_9_DEFAULT =
  "<strong>Most executives at your level who work with a strategist describe the same thing:</strong> they didn't know what they didn't know. The gap between self-directed and coached transitions is measurable — 47% higher comp outcomes on average."

export const QUIZ_SCREENS = [
  {
    id: 1,
    type: "question",
    questionNumber: "Question 1",
    title: "Which of these best describes where you are right now?",
    answerKey: "situation",
    options: [
      {
        answer: "laid-off",
        label: "I was recently laid off or let go — and I want to land something better than what I had."
      },
      { answer: "stalled", label: "I'm employed but I keep getting passed over. I'm stuck and I don't know why." },
      {
        answer: "burnout",
        label: "I've built a successful career. But something feels deeply wrong and I'm running on empty."
      },
      { answer: "pivot", label: "I know I'm in the wrong career entirely. I need a completely different direction." },
      {
        answer: "accelerate",
        label: "I want to move to the C-suite, a significantly larger role, or a board position."
      }
    ]
  },
  {
    id: 2,
    type: "question",
    questionNumber: "Question 1",
    title: "What is your current (or most recent) role level?",
    answerKey: "roleLevel",
    options: [
      { answer: "ic", label: "Individual Contributor / Manager" },
      { answer: "senior-mgr", label: "Senior Manager / Associate Director" },
      { answer: "director", label: "Director" },
      { answer: "vp", label: "Vice President / SVP" },
      { answer: "c-suite", label: "C-Suite (CEO, COO, CFO, CTO, CMO, etc.)" },
      { answer: "board", label: "Board Member / Advisor" }
    ]
  },
  {
    id: 3,
    type: "question",
    questionNumber: "Question 2",
    title: "What industry are you in (or coming from)?",
    answerKey: "industry",
    options: [
      { answer: "tech", label: "Technology / Software" },
      { answer: "finserv", label: "Financial Services / Banking" },
      { answer: "healthcare", label: "Healthcare / Life Sciences" },
      { answer: "consulting", label: "Consulting / Professional Services" },
      { answer: "consumer", label: "Consumer / Retail" },
      { answer: "industrial", label: "Manufacturing / Industrial" },
      { answer: "other-ind", label: "Other" }
    ]
  },
  {
    id: 4,
    type: "question",
    questionNumber: "Question 3",
    title: "What is your current (or most recent) total annual compensation?",
    answerKey: "comp",
    options: [
      { answer: "u120", label: "Under $120K" },
      { answer: "120-180", label: "$120K–$180K" },
      { answer: "180-250", label: "$180K–$250K" },
      { answer: "250-350", label: "$250K–$350K" },
      { answer: "350-500", label: "$350K–$500K" },
      { answer: "o500", label: "Over $500K" },
      { answer: "prefer-not", label: "Prefer not to say" }
    ]
  },
  {
    id: 5,
    type: "question",
    questionNumber: "Question 4",
    title: "How long have you been in your current situation?",
    answerKey: "timeInSituation",
    calloutTriggers: ["6-12m", "gt12m"],
    options: [
      { answer: "lt1m", label: "Less than 1 month" },
      { answer: "1-3m", label: "1–3 months" },
      { answer: "3-6m", label: "3–6 months" },
      { answer: "6-12m", label: "6–12 months" },
      { answer: "gt12m", label: "More than 12 months" }
    ]
  },
  {
    id: 6,
    type: "insight",
    icon: "⊙",
    title: "Good. Let's understand your career capital.",
    body: "The next few questions are about your track record—the measurable foundation of your negotiating power."
  },
  {
    id: 7,
    type: "question",
    questionNumber: "Question 5",
    title: "How many direct reports have you managed at your peak?",
    answerKey: "directReports",
    options: [
      { answer: "0", label: "0 (individual contributor)" },
      { answer: "1-5", label: "1–5" },
      { answer: "6-15", label: "6–15" },
      { answer: "16-50", label: "16–50" },
      { answer: "50p", label: "50+" }
    ]
  },
  {
    id: 8,
    type: "question",
    questionNumber: "Question 6",
    title: "In your last performance review cycle, where did you land?",
    answerKey: "performance",
    options: [
      { answer: "top", label: "Top performer / exceeds expectations" },
      { answer: "strong", label: "Strong performer / meets all expectations" },
      { answer: "mixed", label: "Mixed results / meets some expectations" },
      { answer: "no-review", label: "Did not receive a formal review" },
      { answer: "na", label: "Not applicable — I was recently hired or in transition" }
    ]
  },
  {
    id: 9,
    type: "question",
    questionNumber: "Question 7",
    title: "Have you ever worked with an executive coach or career strategist before?",
    answerKey: "coachingExp",
    calloutTriggers: ["coach-never"],
    defaultCalloutHtml: CALLOUT_9_DEFAULT,
    options: [
      { answer: "coach-yes", label: "Yes — and it was valuable" },
      { answer: "coach-no-val", label: "Yes — but it wasn't helpful" },
      { answer: "coach-cons", label: "No — I've considered it but haven't acted" },
      { answer: "coach-never", label: "No — I've never considered it" }
    ]
  },
  {
    id: 10,
    type: "question",
    questionNumber: "Question 8",
    title: "What is the single biggest obstacle to your next career move right now?",
    answerKey: "primaryObstacle",
    options: [
      { answer: "clarity", label: "I don't know what I want next" },
      { answer: "how", label: "I know what I want but don't know how to get there" },
      { answer: "network", label: "I lack the right network or connections" },
      { answer: "positioning", label: "My resume and positioning don't reflect my true value" },
      { answer: "paralysis", label: "I'm stuck in analysis paralysis — too many options" },
      { answer: "comp", label: "Compensation expectations — I can't find roles that match my current level" },
      { answer: "fear", label: "Fear of making the wrong move" }
    ]
  },
  {
    id: 11,
    type: "question",
    questionNumber: "Question 9",
    title: "When you negotiated your most recent compensation package, how many competing offers did you have?",
    answerKey: "negotiationLeverage",
    options: [
      { answer: "two-plus", label: "2+ competing offers — strong leverage" },
      { answer: "one", label: "1 competing offer — some leverage" },
      { answer: "none", label: "No competing offers — negotiated on merit alone" },
      { answer: "not-neg", label: "I didn't negotiate — accepted as presented" },
      { answer: "never-neg", label: "I've never negotiated my compensation" }
    ]
  },
  {
    id: 12,
    type: "insight",
    icon: "→",
    title: "Almost done. Two more questions about what success looks like for you.",
    body: "Your answers are already painting a clear picture of where you need to go next."
  },
  {
    id: 13,
    type: "question",
    questionNumber: "Question 10",
    title: "In the next 12 months, what is the single most important career outcome?",
    answerKey: "primaryGoal",
    options: [
      { answer: "new-role", label: "Land a new role at a higher level" },
      { answer: "higher-comp", label: "Significantly increase my compensation (at this company or a new one)" },
      { answer: "switch", label: "Make a successful career or industry transition" },
      { answer: "promote", label: "Get promoted to the next level internally" },
      { answer: "clarity-goal", label: "Gain clarity on my long-term career direction" },
      { answer: "meaningful", label: "Reduce burnout and find work that feels meaningful" }
    ]
  },
  {
    id: 14,
    type: "question",
    questionNumber: "Question 11",
    title: "What has most stopped you from taking action sooner?",
    answerKey: "blocker",
    options: [
      { answer: "where-start", label: "I didn't know where to start" },
      { answer: "busy", label: "I've been too busy with current demands" },
      { answer: "resolve", label: "I was hoping the situation would resolve itself" },
      { answer: "risk-averse", label: 'I\'m risk-averse about making a change while things are "fine"' },
      { answer: "doubt", label: "I didn't believe an outside advisor could help at my level" },
      { answer: "deliberation", label: "I've been stuck in deliberation — uncomfortable but not enough to act" },
      { answer: "active", label: "I've been actively working on this" }
    ]
  },
  {
    id: 15,
    type: "emailGate"
  },
  {
    id: 16,
    type: "report"
  }
]

export function useQuestionnaireState() {
  const answers = ref({})
  const currentScreenId = ref(1)

  const currentScreen = computed(() => QUIZ_SCREENS.find((s) => s.id === currentScreenId.value))

  const currentQIndex = computed(() => {
    const idx = QUESTION_SCREENS.indexOf(currentScreenId.value)
    return idx >= 0 ? idx : -1
  })

  const currentQNumber = computed(() => (currentQIndex.value >= 0 ? currentQIndex.value : null))

  const progressPercent = computed(() => {
    const completed = currentQIndex.value >= 0 ? currentQIndex.value : QUESTION_SCREENS.length
    return Math.min((completed / TOTAL_QUESTIONS) * 100, 100)
  })

  function setAnswer(key: any, value: any) {
    answers.value = { ...answers.value, [key]: value }
  }

  function goToScreen(id: number) {
    currentScreenId.value = id
    window.scrollTo(0, 0)
  }

  function advance() {
    const next = currentScreenId.value + 1
    goToScreen(next)
  }

  function previous() {
    if (currentScreenId.value > 2) {
      const previous = currentScreenId.value - 1
      goToScreen(previous)
    }
  }

  return {
    answers,
    currentScreen,
    currentScreenId,
    currentQNumber,
    progressPercent,
    setAnswer,
    goToScreen,
    advance,
    previous
  }
}

// Scoring and report helpers
export function getMarketRate(level: string, industry: string) {
  const rates = {
    ic: { tech: 170, finserv: 155, consulting: 145, healthcare: 135, consumer: 130, industrial: 125, "other-ind": 135 },
    "senior-mgr": {
      tech: 200,
      finserv: 190,
      consulting: 180,
      healthcare: 160,
      consumer: 155,
      industrial: 145,
      "other-ind": 158
    },
    director: {
      tech: 280,
      finserv: 270,
      consulting: 260,
      healthcare: 225,
      consumer: 210,
      industrial: 200,
      "other-ind": 220
    },
    vp: { tech: 380, finserv: 370, consulting: 350, healthcare: 310, consumer: 280, industrial: 270, "other-ind": 290 },
    "c-suite": {
      tech: 520,
      finserv: 510,
      consulting: 490,
      healthcare: 420,
      consumer: 400,
      industrial: 380,
      "other-ind": 410
    },
    board: {
      tech: 155,
      finserv: 155,
      consulting: 152,
      healthcare: 135,
      consumer: 132,
      industrial: 130,
      "other-ind": 142
    }
  }

  const rateLevel = rates[level as keyof typeof rates]
  return (rateLevel && rateLevel[industry as keyof typeof rateLevel]) || 250
}

export function getCompBand(comp: string) {
  if (comp === "prefer-not") return 250
  const map = { u120: 100, "120-180": 150, "180-250": 215, "250-350": 300, "350-500": 425, o500: 600 }
  return map[comp as keyof typeof map] || 250
}

interface QuestionnaireScore {
  roleLevel: string
  performance: string
  directReports: string
  negotiationLeverage: string
  timeInSituation: string
  industry: string
}

export function computeScore(a: QuestionnaireScore) {
  let score = 50
  // Level bonus (all levels get some credit — IC/mgr have growth potential)
  if (["vp", "c-suite", "board"].includes(a.roleLevel)) score += 8
  else if (a.roleLevel === "director") score += 5
  else if (a.roleLevel === "senior-mgr") score += 3
  else if (a.roleLevel === "ic") score += 2
  // Performance
  if (a.performance === "top") score += 8
  else if (a.performance === "strong") score += 4
  else if (a.performance === "mixed") score -= 3
  // Team size (IC with 0 reports is expected, not a penalty)
  if (a.directReports === "50p") score += 5
  else if (a.directReports === "16-50") score += 3
  else if (a.directReports === "6-15") score += 1
  else if (a.directReports === "1-5") score += 1
  // Negotiation weakness (softer penalty for IC — less negotiation exp is normal)
  const isEarlyCareer = a.roleLevel === "ic" || a.roleLevel === "senior-mgr"
  if (a.negotiationLeverage === "never-neg") score -= isEarlyCareer ? 4 : 8
  else if (a.negotiationLeverage === "not-neg") score -= isEarlyCareer ? 2 : 5
  else if (a.negotiationLeverage === "none") score -= 2
  // Time urgency
  if (a.timeInSituation === "gt12m") score -= 6
  else if (a.timeInSituation === "6-12m") score -= 3
  else if (a.timeInSituation === "lt1m") score += 4
  else if (a.timeInSituation === "1-3m") score += 2
  // Industry modifier
  if (["tech", "finserv"].includes(a.industry)) score += 3
  return Math.max(32, Math.min(85, score))
}

export function getPathContent(situation: string, email: string) {
  const goToCheckout = () => {
    const checkoutUrl =
      import.meta.env.VITE_ENV === "prod"
        ? `https://buy.stripe.com/9B65kC0Ad7wy1Ox2QT6EU1N?locked_prefilled_email=${email}`
        : `https://checkout.jobtest.org/b/test_3cI9ASfv7g349gZdvx6EU0B?locked_prefilled_email=${email}`

    // Open the billing portal in the same tab (needed for safari)
    return (window.location.href = checkoutUrl)
  }

  const paths = {
    "laid-off": {
      title: "Your Layoff Is a Pivot Point \u2014 But Only If You Move Before Replacement Mode Sets In",
      scoreName: "Upgrade Probability Score",
      scoreLabel: "Gap Between Your Capability and Your Current Trajectory",
      compType: "market",
      compLabel: "Market Compensation Analysis",
      compMarketMultiplier: 1.175,
      strategies: [
        {
          title: "Compress Your Search Timeline Without Narrowing Your Target",
          desc: "The professionals who land the upgrade are not the ones who apply fastest \u2014 they\u2019re the ones who get into targeted conversations before the role is posted. 70-80% of Director and VP roles are filled through direct network activation before a job description exists. Mass applying on LinkedIn is a strategy for finding a job. It is not a strategy for finding a better one.",
          outcome: "Clients land 2\u20134 offers in an average of 8 weeks vs. 16+ weeks solo"
        },
        {
          title: "Anchor Your Next Title One Level Above What You Lost",
          desc: "The single most common mistake after a layoff is leading with the title you left. That title anchors every conversation to lateral replacement. The first 30 seconds of every networking conversation determines whether you\u2019re being considered for a step up or a step across \u2014 and it\u2019s only recoverable if you correct it before the other person has formed a view.",
          outcome: "73% of coached clients land at a higher title than the one they left"
        },
        {
          title: "Create Competing Offers Before You Need Them",
          desc: "An offer accepted without a competing offer is worth 15-25% less on average than the same candidate who arrives with an alternative. The time to build competing options is not when you have an offer in hand. It is in the first 30 days of the search, when you still have the luxury of moving slowly enough to build them.",
          outcome: "Competing offers increase final package value by 15\u201325% on average"
        }
      ],
      coachName: "Linda Lutz",
      coachTitle: "Founder, Executive Career Coach. 500+ executive transitions managed.",
      coachBio:
        "Has helped 150+ laid-off Director and VP professionals turn their severance window into a strategic upgrade \u2014 not a lateral replacement. Specializes in compressing timelines and building the leverage most candidates don\u2019t know they can manufacture.",
      ctaText: "Book Your Upgrade Strategy Call \u2192",
      stickyLabel: "Ready to land the upgrade?",
      stickyCta: "Book Your Upgrade Strategy Call \u2192",
      ctaAction: goToCheckout,
      testimonials: [
        {
          quote:
            "Laid off from my VP role in March. Decided not to panic. Followed the upgrade strategy \u2014 four offers in 11 weeks. Landed a SVP role at a competitor at 30% higher comp.",
          attr: "David K., 45 | Tech | VP \u2192 SVP"
        },
        {
          quote:
            "I was about to accept the first offer I got because the financial pressure felt unbearable. My coach talked me off the ledge. Three weeks later I had three offers and took the one that was actually the right move.",
          attr: "Priya M., 41 | Healthcare | Director \u2192 VP"
        },
        {
          quote:
            "I thought my layoff was a setback. It turned out to be the only moment I had enough leverage to make the move I\u2019d been wanting to make for three years.",
          attr: "James T., 48 | Finance | Director \u2192 Managing Director"
        }
      ]
    },
    stalled: {
      title: "Your Promotion Is a Visibility Problem \u2014 Not a Performance Problem",
      scoreName: "Visibility Gap Score",
      scoreLabel: "Significant Gap Detected",
      compType: "market",
      compLabel: "Market Compensation Analysis",
      compMarketMultiplier: 1.0,
      strategies: [
        {
          title: "Stay vs. Leave Analysis",
          desc: "A structured decision framework applied to your specific situation: comp trajectory if you stay, realistic options if you leave, and the specific conditions that would change your calculus.",
          outcome: "Clients report making a decision 3\u20134x faster than DIY deliberation"
        },
        {
          title: "Comp Intelligence Briefing",
          desc: "You cannot make a stay vs. leave decision without knowing what you would likely land at your next company. Your briefing gives you the actual market rate for your level, function, and industry \u2014 not Glassdoor averages. This changes the math.",
          outcome: "82% of clients discover they\u2019re underpaid relative to external market"
        },
        {
          title: "Visibility Architecture",
          desc: "Translate your operational impact into the specific language promotion committees use. Most high performers cannot do this. The skill being tested is whether you can frame your work as organizational impact, not personal output. We build this narrative in one session.",
          outcome: "Clients who apply the 60-Day Rule reach a clear decision in avg 47 days vs. 6+ months"
        }
      ],
      coachName: "Linda Lutz",
      coachTitle: "Executive Career Coach. 500+ executive transitions managed.",
      coachBio:
        "Has coached 80+ executives who were getting passed over despite outperforming their peers. Knows how to translate invisible impact into promotion-ready language, force a 60-day decision from leadership, and build the external leverage that changes the internal conversation.",
      ctaText: "Book Your Breakthrough Call \u2192",
      stickyLabel: "Ready to fix your trajectory?",
      stickyCta: "Book Your Breakthrough Call \u2192",
      ctaAction: goToCheckout,
      testimonials: [
        {
          quote:
            "Passed over 3 times. Documented impact, made case to leadership. Promoted to SVP 6 months later at same company. Way more leverage.",
          attr: "Marcus T., 48 | Tech | Senior Director \u2192 SVP"
        },
        {
          quote:
            "Invisible at my company. Tested market, got 4 external offers. Went back to my manager with that proof. Got promoted + 25% raise.",
          attr: "Elena R., 46 | Pharma | VP \u2192 Senior VP"
        },
        {
          quote:
            "Was convinced I\u2019d topped out. Spent 60 days making promotion case. Didn\u2019t work. Took external role at 2 levels up. Stayed only 18 months but proved I wasn\u2019t stalled \u2014 the company was.",
          attr: "Tom B., 50 | Finance | Director \u2192 SVP"
        }
      ]
    },
    burnout: {
      title:
        "What You\u2019re Describing Isn\u2019t Burnout From Overwork \u2014 It\u2019s Something Harder to Fix and More Fixable",
      scoreName: "Direction Clarity Score",
      scoreLabel: "High Ambiguity \u2014 Direction Gap Identified",
      compType: "market",
      compLabel: "Transition Financial Analysis",
      compMarketMultiplier: 1.0,
      strategies: [
        {
          title: "Energy Audit & Direction Architecture",
          desc: "Map the specific conditions at work that drive your energy vs. drain it. This isn\u2019t a personality assessment \u2014 it\u2019s a structured analysis of your highest-energy moments across your career to identify what \u2018right\u2019 actually means for you.",
          outcome: "Clients identify their non-negotiable conditions in a single session"
        },
        {
          title: "Transferability Mapping",
          desc: "Your experience transfers \u2014 but the transferability isn\u2019t obvious until someone maps it. We identify which roles, sectors, and companies align with your conditions and can defend your current comp level.",
          outcome: "91% of clients maintain or increase comp through coached transitions"
        },
        {
          title: "Role Architecture Negotiation",
          desc: "Scope, remote flexibility, team size, manager quality, and operating rhythm are all negotiable at your level \u2014 and often worth more than a comp bump. We identify which variables will determine whether you\u2019re still burned out eighteen months from now.",
          outcome: "Clients who negotiate role architecture report 3x higher satisfaction at 12 months"
        }
      ],
      coachName: "Linda Lutz",
      coachTitle: "Founder, Executive Career Coach. 500+ executive transitions managed.",
      coachBio:
        "Has helped 60+ senior professionals who had built the career they thought they wanted reconstruct the one they actually needed \u2014 without starting over financially or professionally. Specializes in direction architecture and meaning-preserving transitions.",
      ctaText: "Book Your Direction Clarity Call \u2192",
      stickyLabel: "Ready for a different direction?",
      stickyCta: "Book Your Direction Clarity Call \u2192",
      ctaAction: goToCheckout,
      testimonials: [
        {
          quote:
            "I had the title, the comp, the team. I was miserable and I didn\u2019t know why. Six months after working with my coach I was in a role that paid within 10% of my old one and I actually wanted to go to work on Monday.",
          attr: "Sarah L., 46 | Consulting | VP \u2192 Director of Strategy, Mission-Driven Org"
        },
        {
          quote:
            "I thought I needed to leave my industry entirely. My coach helped me realize I needed to leave my company. Same field, better culture. The change took four months and I got a 12% raise.",
          attr: "Michael D., 51 | Finance | Managing Director"
        },
        {
          quote:
            "I spent two years researching the \u2018right\u2019 next move. One coaching engagement gave me more clarity than all of it combined \u2014 because it was about me specifically, not careers in general.",
          attr: "Rachel T., 43 | Tech | SVP \u2192 Chief of Staff, Series C Company"
        }
      ]
    },
    pivot: {
      title:
        "The Gap Between \u2018I Know My Strengths\u2019 and \u2018I Know My Next Move\u2019 Is Exactly What Coaching Closes",
      scoreName: "Transition Readiness Score",
      scoreLabel: "Moderate Readiness \u2014 Key Gaps Identified",
      compType: "market",
      compLabel: "Pivot Financial Analysis",
      compMarketMultiplier: 1.0,
      strategies: [
        {
          title: "Map Your Skills to Markets That Actually Hire for Them",
          desc: "Most career assessments identify your skills. None of them tell you which industries are currently paying for those skills at your experience level. The gap between \u2018I\u2019m good at X\u2019 and \u2018there\u2019s a market for X at $Y\u2019 is the gap that derails most career changes. The specific mistake is pivoting toward a field that sounds like a fit without confirming that the field hires laterally at your level. Many attractive industries have strong internal pipelines and almost never hire senior talent from outside.",
          outcome: "Clients who validate market demand first avoid the avg 22% income hit of blind pivots"
        },
        {
          title: "Build Your Bridge Narrative Before You Need It",
          desc: "The most common pivot failure is not lack of qualification \u2014 it\u2019s inability to answer \u2018why are you making this change?\u2019 in a way that builds confidence rather than doubt. Hiring managers for senior lateral pivots need to believe three things: you have transferable value, you\u2019re not fleeing something, and you\u2019re going to stay. Your narrative must address all three without being asked. Most candidates address none of them and wonder why they\u2019re not advancing.",
          outcome: "68% of coached pivots maintain or increase compensation vs. 41% who go solo"
        },
        {
          title: "Run a Low-Cost Market Test Before You Commit",
          desc: "The most expensive mistake in a career pivot is discovering the target field isn\u2019t what you imagined after you\u2019ve already committed publicly. Informational interviews used correctly \u2014 not for networking but for field intelligence \u2014 will tell you more about fit than any assessment. Ten conversations structured around \u2018Who succeeds here and who doesn\u2019t?\u2019 will answer the question you\u2019ve been Googling for months.",
          outcome: "10 structured conversations surface more fit data than 6 months of independent research"
        }
      ],
      coachName: "Linda Lutz",
      coachTitle: "Founder, Executive Career Coach. 500+ executive transitions managed.",
      coachBio:
        "Has helped 100+ professionals make successful career pivots \u2014 from identifying a validated direction to executing the transition without the income disruption most people fear. Specializes in translating transferable experience into language that opens new doors.",
      ctaText: "Book Your Pivot Blueprint Call \u2192",
      stickyLabel: "Ready to find your direction?",
      stickyCta: "Book Your Pivot Blueprint Call \u2192",
      ctaAction: goToCheckout,
      testimonials: [
        {
          quote:
            "I\u2019d done StrengthsFinder, Myers-Briggs, every quiz on the internet. I still didn\u2019t know what to do. My coach identified three realistic industries I\u2019d never considered that were actively hiring for exactly what I\u2019d been doing. I\u2019m now six months into one of them.",
          attr: "Alicia M., 37 | Former Education \u2192 Learning & Development, Tech"
        },
        {
          quote:
            "I was terrified I\u2019d have to take a massive pay cut to make a change. I didn\u2019t. Same comp, totally different work. The key was finding the frame that made my old experience an asset in the new field instead of a liability.",
          attr: "Chris W., 41 | Former Legal \u2192 Operations, Healthcare"
        },
        {
          quote:
            "I\u2019d been \u2018about to make a change\u2019 for three years. The coaching forced me to test my assumptions instead of just researching them. I found out two of my three target industries would have been dead ends. The third was perfect.",
          attr: "Nina P., 39 | Former Marketing \u2192 Product Strategy"
        }
      ]
    },
    accelerate: {
      title: "The Move You\u2019re Targeting Is Access-Dependent, Not Resume-Dependent",
      scoreName: "Access Gap Score",
      scoreLabel: "Network Depth Detected \u2014 Breadth Gap Identified",
      compType: "market",
      compLabel: "Opportunity Cost Analysis",
      compMarketMultiplier: 1.0,
      strategies: [
        {
          title: "Activate Network Depth Toward Your Target",
          desc: "Your current network is the reason you\u2019ve gotten every role you\u2019ve had. It is also the ceiling on what\u2019s visible to you. The roles that represent your next meaningful step are almost certainly not being surfaced through your current relationships \u2014 because your current relationships are insiders to the world you\u2019re already in. The specific work is identifying second-degree connections between your current network and your target sector, then creating deliberate, non-transactional reasons to build those bridges before you need them.",
          outcome: "Executives who build cross-sector bridges land roles invisible to 90%+ of candidates"
        },
        {
          title: "Control Your Narrative Before Recruiters Write It for You",
          desc: "At the executive level, your narrative exists whether you manage it or not. Executive search firms are forming views about what you\u2019re suited for based on your last two titles \u2014 and those views tend to be conservative and backward-looking. The executives who land the highest-quality opportunities are almost never discovered by recruiters. They are already known to the decision-maker before the search begins. Managing your narrative means creating the conditions for that prior relationship to exist.",
          outcome: "Coached executives are 4x more likely to be approached for unlisted C-suite roles"
        },
        {
          title: "Structure the Move for Maximum Optionality",
          desc: "The variables with the highest long-term financial impact at the executive level are almost never discussed in the offer conversation: equity structure, board dynamics, CEO relationship quality, and the specific growth trajectory of the business at the moment of entry. An executive joining a high-trajectory company with favorable equity terms at the right moment often generates more wealth from that single decision than from fifteen years of salary optimization. The coaching work at this stage is knowing which variables to evaluate and how to weight them.",
          outcome: "Clients who negotiate equity terms with coaching capture $150K\u2013$400K more on average"
        }
      ],
      coachName: "Linda Lutz",
      coachTitle: "Founder, Executive Career Coach. 500+ executive transitions managed.",
      coachBio:
        "Works with C-suite and board-level professionals on high-stakes career moves. Has direct relationships with executive search partners, PE operating teams, and board networks across tech, finance, and healthcare. Specializes in access creation and narrative control for moves that aren\u2019t posted publicly.",
      ctaText: "Book Your Executive Strategy Call \u2192",
      stickyLabel: "Ready to make the right move?",
      stickyCta: "Book Your Executive Strategy Call \u2192",
      ctaAction: goToCheckout,
      testimonials: [
        {
          quote:
            "I\u2019d been approached by recruiters for 18 months with roles that weren\u2019t right. One coaching engagement changed my approach entirely \u2014 I stopped waiting to be found and started building the relationships that mattered. I accepted a CRO role six months later through a direct introduction, not a recruiter.",
          attr: "Mark S., 52 | Tech | SVP Sales \u2192 CRO"
        },
        {
          quote:
            "I thought I knew how to navigate a C-suite job search. I was wrong about the equity conversation. My coach identified $800K in equity terms I would have left on the table without knowing they were negotiable.",
          attr: "Jennifer R., 49 | Finance | CFO"
        },
        {
          quote:
            "Three PE firms were circling me but none of them were the right fit. My coach helped me get in front of two operating partners I\u2019d never have reached on my own. Took the second engagement \u2014 exactly the role I\u2019d been describing for two years.",
          attr: "Robert C., 54 | Healthcare | CEO"
        }
      ]
    }
  }
  return paths[situation as keyof typeof paths] || paths["stalled"]
}

export function adaptContentForLevel(content: any, roleLevel: string) {
  if (!roleLevel) return content
  // Director+ gets the original executive-focused content
  if (["director", "vp", "c-suite", "board"].includes(roleLevel)) return content

  // Deep clone to avoid mutating the original
  content = JSON.parse(JSON.stringify(content))

  // Adapt coach bio + title
  content.coachTitle = content.coachTitle
    .replace(/Executive Career Coach/gi, "Career Coach & Strategist")
    .replace(/500\+ executive transitions managed/gi, "500+ career transitions managed")

  content.coachBio = content.coachBio
    .replace(/150\+ laid-off Director and VP professionals/gi, "150+ professionals at every career stage")
    .replace(/80\+ executives who were getting passed over/gi, "80+ professionals who were getting passed over")
    .replace(/60\+ senior professionals/gi, "60+ professionals")
    .replace(/100\+ professionals make successful career pivots/gi, "100+ professionals make successful career pivots")
    .replace(
      /C-suite and board-level professionals on high-stakes career moves/gi,
      "professionals at every stage on strategic career moves"
    )
    .replace(
      /executive search partners, PE operating teams, and board networks/gi,
      "hiring decision-makers and talent networks"
    )
    .replace(/executive transitions/gi, "career transitions")

  // Adapt CTA labels
  content.ctaText = content.ctaText
    .replace(/Executive Strategy/gi, "Career Strategy")
    .replace(/Upgrade Strategy/gi, "Career Strategy")
  content.stickyCta = content.stickyCta
    .replace(/Executive Strategy/gi, "Career Strategy")
    .replace(/Upgrade Strategy/gi, "Career Strategy")

  // Adapt testimonials — IC/mgr-appropriate versions
  const sitTestimonials = {
    "laid-off": [
      {
        quote:
          "Lost my job in a reorg and almost panic-applied to everything. My coach helped me rewrite my positioning in week one. I had three interviews within two weeks — and the role I took was a step up, not a lateral.",
        attr: "Jason M., 31 | Tech | Senior Analyst → Lead Strategist"
      },
      {
        quote:
          "I was convinced being laid off at my level meant I’d have to take a pay cut. Wrong. My coach showed me how to reframe my experience. Landed at a competitor for 22% more.",
        attr: "Priya S., 34 | Healthcare | Project Manager → Senior PM"
      },
      {
        quote:
          "The hardest part wasn’t the job search — it was knowing what to say in the first 30 seconds of every conversation. Once we fixed that, everything moved faster.",
        attr: "Carlos R., 29 | Finance | Analyst → Associate"
      }
    ],
    stalled: [
      {
        quote:
          "I kept doing great work and assuming someone would notice. Nobody did. My coach helped me build a visibility plan — promoted within four months.",
        attr: "Taylor K., 33 | Tech | Senior Engineer → Staff Engineer"
      },
      {
        quote:
          'I was told there was "no room" for a promotion. My coach helped me test the market. Came back with two offers and suddenly there was room.',
        attr: "Danielle R., 36 | Consumer | Manager → Senior Manager"
      },
      {
        quote:
          "I spent a year hoping my annual review would finally go my way. One coaching session helped me realize I was solving the wrong problem.",
        attr: "Ryan C., 30 | Consulting | Associate → Engagement Manager"
      }
    ],
    burnout: [
      {
        quote:
          "I thought I needed to change industries entirely. Turns out I needed to change my manager, my scope, and my commute. Same field, completely different energy.",
        attr: "Mei L., 35 | Tech | Product Manager"
      },
      {
        quote:
          "I was so burned out I couldn’t think clearly about next steps. My coach gave me a framework to evaluate what was actually draining me vs. what I assumed.",
        attr: "Andre J., 32 | Finance | Senior Analyst"
      },
      {
        quote:
          "I didn’t need a career change — I needed a role redesign. My coach helped me negotiate remote flexibility and a scope adjustment. Same company, completely different job.",
        attr: "Sarah P., 38 | Healthcare | Program Manager"
      }
    ],
    pivot: [
      {
        quote:
          "I’d taken every career quiz on the internet. Still stuck. My coach mapped my actual skills to three industries I’d never considered — I’m now six months into one of them.",
        attr: "Alicia M., 34 | Former Education → L&D, Tech"
      },
      {
        quote:
          "I was terrified of a pay cut. Didn’t happen. My coach helped me find the frame that made my old experience an asset in the new field.",
        attr: "Chris W., 36 | Former Legal → Operations, Healthcare"
      },
      {
        quote:
          'I’d been "about to make a change" for two years. Coaching forced me to test my assumptions instead of researching them. Found out two of my three target fields were dead ends. The third was perfect.',
        attr: "Nina P., 31 | Former Marketing → Product Strategy"
      }
    ],
    accelerate: [
      {
        quote:
          "I wanted the next level but didn’t know how to position myself for it. My coach helped me build a 90-day plan that made the promotion case obvious.",
        attr: "Marcus T., 33 | Tech | Engineer → Senior Engineer + Tech Lead"
      },
      {
        quote:
          "I thought I needed more years of experience. My coach showed me I needed a different narrative. Got promoted within 6 months.",
        attr: "Elena R., 30 | Finance | Analyst → Senior Associate"
      },
      {
        quote:
          'Every mentor told me to "be patient." My coach gave me a strategy. I wasn’t impatient — I was under-positioned.',
        attr: "Jordan K., 35 | Consulting | Manager → Senior Manager"
      }
    ]
  }

  // Determine situation from scoreName
  const sitKey =
    content.compMarketMultiplier === 1.175
      ? "laid-off"
      : content.scoreName === "Visibility Gap Score"
        ? "stalled"
        : content.scoreName === "Direction Clarity Score"
          ? "burnout"
          : content.scoreName === "Transition Readiness Score"
            ? "pivot"
            : content.scoreName === "Access Gap Score"
              ? "accelerate"
              : null

  if (sitKey && sitTestimonials[sitKey]) {
    content.testimonials = sitTestimonials[sitKey]
  }

  // Adapt strategy descriptions
  content.strategies = content.strategies.map((s: { title: string; desc: string; outcome: string }) => {
    let d = s.desc
    d = d.replace(/at the executive level/gi, "at your level")
    d = d.replace(/An executive joining/gi, "A professional joining")
    d = d.replace(/Coached executives/gi, "Coached professionals")
    d = d.replace(/Executives who/gi, "Professionals who")
    d = d.replace(/Director and VP roles/gi, "roles at the next level")
    d = d.replace(/Director or VP/gi, "higher-level")
    d = d.replace(/C-Suite/gi, "senior leadership")
    d = d.replace(/board credibility/gi, "professional credibility")
    d = d.replace(/PE operating teams/gi, "hiring teams")
    d = d.replace(/executive search/gi, "talent search")
    d = d.replace(/board dynamics/gi, "team dynamics")
    d = d.replace(/CEO relationship quality/gi, "manager relationship quality")
    d = d.replace(/equity structure/gi, "total comp structure")
    d = d.replace(/equity terms/gi, "comp terms")
    d = d.replace(/equity acceleration/gi, "comp acceleration")
    d = d.replace(/\bexecutive\b/gi, "professional")
    let o = s.outcome || ""
    o = o.replace(/Executives who/gi, "Professionals who")
    o = o.replace(/Coached executives/gi, "Coached professionals")
    o = o.replace(/C-suite roles/gi, "higher-level roles")
    o = o.replace(/\bexecutive\b/gi, "professional")
    return { title: s.title, desc: d, outcome: o }
  })

  return content
}

export const FAQ_ITEMS = [
  {
    q: "What happens in a strategy call?",
    a: "We will review your current situation, clarify what is realistic for your level/industry, identify your biggest blocker, and give you 3–5 specific next steps."
  },
  {
    q: "How long does a typical career transition take?",
    a: "Most professionals see offers 60–90 days after starting their search. Comp negotiation takes another 2–3 weeks. Total: 3–4 months from start to offer."
  },
  {
    q: "Do you work with remote/hybrid roles?",
    a: "Yes. We help with both. Remote often reduces competition but also attracts more candidates, so positioning matters more."
  },
  {
    q: "What if I am not sure I want to leave?",
    a: "That is normal. A strategy call helps you test whether staying or going makes sense. Some clients decide to negotiate internally — which we can help with too."
  },
  {
    q: "How do you handle confidentiality?",
    a: "Your data is confidential. We never reach out to recruiters, employers, or HR on your behalf without explicit permission. You control the process."
  }
]

export const FAQ_ITEMS_STALLED = [
  {
    q: "What happens in a strategy call?",
    a: "We review your current situation, clarify what is realistic for your level and industry, identify your biggest blocker, and give you 3–5 specific next steps. You leave with a clear picture of whether the path forward is internal, external, or both."
  },
  {
    q: "How long does a typical career transition take?",
    a: "Most professionals see offers within 60–90 days after starting their search. Comp negotiation takes another 2–3 weeks. Total: 3–4 months from start to offer."
  },
  {
    q: "Do you work with remote/hybrid roles?",
    a: "Yes. We help with both. Remote often reduces competition but also attracts more candidates, so positioning matters more."
  },
  {
    q: "What if I am not sure I want to leave?",
    a: "Most of our clients aren’t sure. The strategy call is designed to help you decide — not to push you toward leaving. We help you make the right decision for your situation, not ours."
  },
  {
    q: "How do you handle confidentiality?",
    a: "Your data is confidential. We never reach out to recruiters, employers, or HR on your behalf without explicit permission. You control the process entirely."
  }
]
